import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchShare, type SharedDeck } from '../lib/api';
import { RENDER_KINDS, RENDER_LABELS, type RenderKind } from '../lib/deck';
import { formatCode, formatCents, formatDimensions } from '../lib/format';
import { PlaceholderRender } from '../components/PlaceholderRender';

export function SharedDeckPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; share: SharedDeck }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  useEffect(() => {
    if (!token) {
      setState({ status: 'error', message: 'No share token provided.' });
      return;
    }
    let cancelled = false;
    fetchShare(token)
      .then((r) => {
        if (!cancelled) setState({ status: 'ready', share: r.share });
      })
      .catch((e) => {
        if (!cancelled) {
          setState({
            status: 'error',
            message: e instanceof Error ? e.message : String(e),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen bg-vari-beige/30">
      {/* Customer-facing header — no internal app chrome */}
      <header className="bg-vari-navy text-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-vari-blue-light">
              Vari · Idea Starter
            </p>
            <h1 className="text-xl font-semibold tracking-tight mt-0.5">A custom proposal</h1>
          </div>
          <a
            href="https://www.vari.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-white/80 hover:text-white"
          >
            vari.com →
          </a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {state.status === 'loading' && (
          <p className="text-vari-gray text-sm">Loading your proposal…</p>
        )}

        {state.status === 'error' && (
          <div className="rounded-2xl border border-vari-red/30 bg-vari-red/5 px-6 py-8 text-center max-w-lg mx-auto">
            <p className="text-vari-navy font-semibold">This link isn’t available</p>
            <p className="text-vari-gray text-sm mt-2">{state.message}</p>
            <p className="text-vari-gray text-xs mt-4">
              If you believe this is a mistake, contact your Vari sales representative.
            </p>
          </div>
        )}

        {state.status === 'ready' && <SharedDeckBody share={state.share} />}
      </main>

      <footer className="border-t border-vari-soft-gray bg-white">
        <div className="max-w-5xl mx-auto px-6 py-5 text-xs text-vari-gray flex items-center justify-between flex-wrap gap-2">
          <span>Prepared for you by Vari · Pricing effective 01/2026.</span>
          <span>© Vari, Inc.</span>
        </div>
      </footer>
    </div>
  );
}

function SharedDeckBody({ share }: { share: SharedDeck }) {
  return (
    <div className="space-y-8">
      {/* Intro */}
      <div className="space-y-2">
        {share.customerName && (
          <p className="text-vari-blue-accessible text-sm">Prepared for {share.customerName}</p>
        )}
        <h2 className="text-3xl font-semibold text-vari-navy tracking-tight">
          {share.name || `${share.items.length} office layouts to consider`}
        </h2>
        <p className="text-vari-gray text-sm">
          {share.items.length} layout{share.items.length === 1 ? '' : 's'} curated by your Vari
          sales representative.
        </p>
      </div>

      {/* Items */}
      <div className="space-y-6">
        {share.items.map((item) => (
          <SharedItemCard key={item.layoutCode} item={item} />
        ))}
      </div>
    </div>
  );
}

function SharedItemCard({
  item,
}: {
  item: SharedDeck['items'][number];
}) {
  return (
    <div className="rounded-2xl border border-vari-soft-gray bg-white overflow-hidden">
      <div className="p-6 sm:p-8 space-y-5">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.25em] text-vari-blue-accessible">
              {item.familyName}
            </p>
            <h3 className="text-2xl sm:text-3xl font-semibold text-vari-navy mt-1">
              {item.familyName} — #{formatCode(item.layoutCode)}
            </h3>
            <p className="text-vari-gray text-sm mt-1">
              {formatDimensions(item.widthInches, item.depthInches)} · {item.itemCount} items
            </p>
          </div>
          {item.includePricing && (
            <p className="text-2xl font-semibold text-vari-navy">
              {formatCents(item.totalPriceCents)}
            </p>
          )}
        </div>

        {item.selectedRenders.length > 0 && (
          <div
            className={`grid gap-3 ${
              item.selectedRenders.length === 1
                ? 'grid-cols-1'
                : item.selectedRenders.length === 2
                  ? 'grid-cols-2'
                  : 'grid-cols-2 sm:grid-cols-3'
            }`}
          >
            {item.selectedRenders.map((kind) => {
              const known = (RENDER_KINDS as readonly string[]).includes(kind)
                ? (kind as RenderKind)
                : null;
              const label = known ? RENDER_LABELS[known] : kind;
              // The share snapshot only records selected slot keys, not URLs.
              // Customers see the same labeled placeholder as the rep until we
              // add render URLs to the snapshot (or fetch them at view time).
              return (
                <div
                  key={kind}
                  className="rounded-lg overflow-hidden border border-vari-soft-gray"
                >
                  <PlaceholderRender variant="card" label={label} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
